import { db, fsTools } from 'config/firebase';

const { collection, onSnapshot, query, where } = fsTools;

const INVITED_STATUS = 'invited';

const clean = value => (typeof value === 'string' ? value.trim() : '');

const secondsOf = timestamp => timestamp?.seconds ?? timestamp?._seconds ?? 0;

// The identities an invite can be addressed to, the same email-or-phone match the API uses
// for GET /invites/me/pending. firestore.rules lets a user read an `invited` invite only
// through a query on one of these two fields, so there is one listener per identity.
export const pendingInviteIdentities = ({ email, phone_number: phoneNumber } = {}) =>
    [
        ['email', clean(email).toLowerCase()],
        ['phone_number', clean(phoneNumber)]
    ].filter(([, value]) => value);

// Live list of the signed-in user's `invited` invites. An invite that is withdrawn, declined,
// accepted or answered elsewhere stops matching and drops out of the list by itself.
//
// `onChange` gets the merged list once every listener has delivered its first snapshot, then
// on every change. `onError` is called per failing listener (e.g. rules not deployed yet).
// Returns the unsubscribe function, or null when the user has neither email nor phone number
// to listen with.
export const watchPendingInvites = (user, { onChange, onError }) => {
    const identities = pendingInviteIdentities(user);

    if (identities.length === 0) {
        return null;
    }

    const invitesByIdentity = new Map();

    const publish = () => {
        if (invitesByIdentity.size < identities.length) return;

        const merged = new Map();

        invitesByIdentity.forEach(invites =>
            invites.forEach(invite => merged.set(invite.invite_id, invite))
        );

        onChange(
            [...merged.values()].sort(
                (a, b) => secondsOf(a.created_at) - secondsOf(b.created_at)
            )
        );
    };

    const unsubscribes = identities.map(([field, value]) =>
        onSnapshot(
            query(
                collection(db, 'invites'),
                where(field, '==', value),
                where('status', '==', INVITED_STATUS)
            ),
            snapshot => {
                invitesByIdentity.set(
                    field,
                    snapshot.docs.map(doc => ({ ...doc.data(), invite_id: doc.id }))
                );
                publish();
            },
            onError
        )
    );

    return () => unsubscribes.forEach(unsubscribe => unsubscribe());
};
